import React, { useEffect, useState } from 'react';
import MainLayout from '../../components/MainLayout';
import HomePageView from './HomePageView';
import { Announcement } from 'types/announcements';
import { RootState } from 'state-management/store';
import { useSelector } from 'react-redux';
import { EUserRole } from 'types/user';
import {
  createAnnouncement,
  deleteAnnouncement,
  listAnnouncements,
  updateAnnouncement,
} from '../../api/announcements';
import {
  createEvent,
  defaultDateForMonth,
  deleteEvent,
  formatCalendarDay,
  HomeEvent,
  listEvents,
  monthKey,
  updateEvent,
} from '../../api/events';
import { apiErrorMessage } from '../../api/http';
import AnadirAnuncioModal, { IAnuncioFormData } from './AnadirAnuncioModal';
import AnadirEventoModal, { IEventoFormData } from './AnadirEventoModal';
import ConfirmarEliminarModal from './ConfirmarEliminarModal';

const HomePage: React.FC = () => {
  const user = useSelector((state: RootState) => state.global.user)!;
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoadingAnnouncements, setIsLoadingAnnouncements] = useState(true);
  const [announcementsError, setAnnouncementsError] = useState<string | null>(null);
  const [events, setEvents] = useState<HomeEvent[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [visibleMonth, setVisibleMonth] = useState(() => monthKey(new Date()));
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState<Announcement | null>(null);
  const [editingEvent, setEditingEvent] = useState<HomeEvent | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<HomeEvent | null>(null);
  const canManage = user.role === EUserRole.ADMINISTRADOR;

  useEffect(() => {
    let cancelled = false;
    listAnnouncements()
      .then((rows) => {
        if (cancelled) return;
        setAnnouncements(rows);
        setAnnouncementsError(null);
      })
      .catch((error: unknown) => {
        if (!cancelled) setAnnouncementsError(apiErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAnnouncements(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingEvents(true);
    setEvents([]);
    listEvents(visibleMonth)
      .then((rows) => {
        if (cancelled) return;
        setEvents(rows);
        setEventsError(null);
      })
      .catch((error: unknown) => {
        if (!cancelled) setEventsError(apiErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEvents(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visibleMonth]);

  const reloadAnnouncements = async () => {
    try {
      setAnnouncements(await listAnnouncements());
      setAnnouncementsError(null);
    } catch (error) {
      setAnnouncementsError(apiErrorMessage(error));
    }
  };

  const reloadEvents = async (month: string) => {
    try {
      setEvents(await listEvents(month));
      setEventsError(null);
    } catch (error) {
      setEventsError(apiErrorMessage(error));
    }
  };

  const handleSaveAnnouncement = async (data: IAnuncioFormData) => {
    if (editingAnnouncement) {
      await updateAnnouncement(editingAnnouncement.id, data);
    } else {
      await createAnnouncement(data);
    }
    setAnnouncementModalOpen(false);
    setEditingAnnouncement(null);
    await reloadAnnouncements();
  };

  const handleDeleteAnnouncement = async () => {
    if (!deletingAnnouncement) return;
    await deleteAnnouncement(deletingAnnouncement.id);
    setDeletingAnnouncement(null);
    await reloadAnnouncements();
  };

  const handleSaveEvent = async (data: IEventoFormData) => {
    if (editingEvent) {
      await updateEvent(editingEvent.id, data);
    } else {
      await createEvent(data);
    }
    setEventModalOpen(false);
    setEditingEvent(null);
    const nextMonth = data.date.slice(0, 7);
    if (nextMonth !== visibleMonth) {
      setVisibleMonth(nextMonth);
      return;
    }
    await reloadEvents(visibleMonth);
  };

  const handleDeleteEvent = async () => {
    if (!deletingEvent) return;
    await deleteEvent(deletingEvent.id);
    setDeletingEvent(null);
    await reloadEvents(visibleMonth);
  };

  const getPageSubtitle = () => {
    switch (user.role) {
      case EUserRole.DOCENTE:
        return 'Portal Docentes';
      case EUserRole.AUDITOR:
        return 'Portal Auditores';
      case EUserRole.ADMINISTRADOR:
        return 'Portal Administradores';
      case EUserRole.ALUMNO:
      default:
        return 'Portal Alumnos';
    }
  };

  const getSemester = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const semester = currentMonth < 6 ? 'A' : 'B';
    return `Semestre ${currentYear}-${semester}`;
  };

  return (
    <MainLayout
      pageTitle={`Bienvenido, ${user.firstName} ${user.lastName}`}
      pageSubtitle={`${getPageSubtitle()} - ${getSemester()}`}
    >
      <HomePageView
        announcements={announcements}
        isLoadingAnnouncements={isLoadingAnnouncements}
        announcementsError={announcementsError}
        events={events}
        isLoadingEvents={isLoadingEvents}
        eventsError={eventsError}
        visibleMonth={visibleMonth}
        canManage={canManage}
        onMonthChange={(month) => setVisibleMonth(monthKey(month))}
        onAddAnnouncement={() => {
          setEditingAnnouncement(null);
          setAnnouncementModalOpen(true);
        }}
        onAddEvent={() => {
          setEditingEvent(null);
          setEventModalOpen(true);
        }}
        onEditAnnouncement={setEditingAnnouncement}
        onDeleteAnnouncement={setDeletingAnnouncement}
        onEditEvent={setEditingEvent}
        onDeleteEvent={setDeletingEvent}
      />
      {canManage && (
        <>
          <AnadirAnuncioModal
            open={announcementModalOpen || editingAnnouncement !== null}
            initialData={
              editingAnnouncement
                ? { title: editingAnnouncement.title, description: editingAnnouncement.description }
                : undefined
            }
            onClose={() => {
              setAnnouncementModalOpen(false);
              setEditingAnnouncement(null);
            }}
            onConfirmar={handleSaveAnnouncement}
          />
          <AnadirEventoModal
            open={eventModalOpen || editingEvent !== null}
            defaultDate={defaultDateForMonth(visibleMonth)}
            initialData={
              editingEvent
                ? {
                    title: editingEvent.title,
                    description: editingEvent.description,
                    date: formatCalendarDay(editingEvent.date),
                  }
                : undefined
            }
            onClose={() => {
              setEventModalOpen(false);
              setEditingEvent(null);
            }}
            onConfirmar={handleSaveEvent}
          />
          <ConfirmarEliminarModal
            open={deletingAnnouncement !== null}
            label='anuncio'
            nombre={deletingAnnouncement?.title ?? ''}
            onCancel={() => setDeletingAnnouncement(null)}
            onConfirm={handleDeleteAnnouncement}
          />
          <ConfirmarEliminarModal
            open={deletingEvent !== null}
            label='evento'
            nombre={deletingEvent?.title ?? ''}
            onCancel={() => setDeletingEvent(null)}
            onConfirm={handleDeleteEvent}
          />
        </>
      )}
    </MainLayout>
  );
};

export default HomePage;
